import i18n.StringKey
import i18n.tr
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.GlobalScope
import kotlinx.coroutines.await
import kotlinx.coroutines.launch
import kotlinx.serialization.Serializable
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import org.w3c.dom.HTMLElement
import org.w3c.fetch.RequestInit

@Serializable
data class CheckoutRequest(val plan: String, val tier: String = "standard", val successUrl: String, val cancelUrl: String)

@Serializable
data class CheckoutResponse(val url: String)

class SubscribePage(
    private val onBackClick: () -> Unit,
    private val isAlreadyTrialing: Boolean = false,
    private val proOnly: Boolean = false,
    private val onNavigate: ((String) -> Unit)? = null,
    private val isLoggedIn: Boolean = false,
    private val onAppNavigate: ((String) -> Unit)? = null,
    private val asModal: Boolean = false,
) {
    private val isMobile = ResponsiveUtils.isMobile()
    private val json = Json { ignoreUnknownKeys = true }
    private val apiService = ApiService()

    fun render(): HTMLElement {
        val container = document.createElement("div") as HTMLElement
        container.style.cssText = if (asModal) """
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
            color: #ffffff;
            display: flex;
            flex-direction: column;
            align-items: center;
            padding: ${if (isMobile) "24px 16px" else "32px 24px"};
        """ else """
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
            background: linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 100%);
            color: #ffffff;
            min-height: 100vh;
            display: flex;
            flex-direction: column;
            align-items: center;
            padding: ${if (isMobile) "24px 16px" else "48px 24px"};
        """

        container.innerHTML = buildSubscribeHTML(isAlreadyTrialing)

        // Self-load subscription status to ensure correct button text regardless of timing
        GlobalScope.launch {
            try {
                val status = apiService.getSubscriptionStatus()
                val actuallyTrialing = status.status == "trialing"
                if (actuallyTrialing != isAlreadyTrialing) {
                    container.innerHTML = buildSubscribeHTML(actuallyTrialing)
                    attachButtonListeners(container)
                }
            } catch (e: Exception) { /* keep existing */ }
        }

        return container
    }

    private fun buildSubscribeHTML(trialing: Boolean): String {
        val cardWidth = if (isMobile) "100%" else "340px"
        val gap = if (isMobile) "16px" else "24px"
        val buttonLabel = if (proOnly) StringKey.PRICING_UPGRADE_NOW.tr()
            else if (trialing) StringKey.PAYWALL_SUBSCRIBE.tr()
            else StringKey.SUBSCRIBE_START_FREE_TRIAL.tr()
        val subtitle = if (proOnly) StringKey.PRICING_UPGRADE_SUBTITLE.tr()
            else if (trialing) StringKey.SUBSCRIBE_TRIALING_SUBTITLE.tr()
            else StringKey.SUBSCRIBE_TRIAL_SUBTITLE.tr()
        val title = if (proOnly) StringKey.PRICING_UPGRADE_TO_PRO.tr() else StringKey.SUBSCRIBE_CHOOSE_PLAN.tr()

        val standardSection = if (proOnly) "" else """
                    <!-- Standard Tier Header -->
                    <div style="width: 100%; max-width: 720px; margin: 0 auto 12px; text-align: center;">
                        <h2 style="font-size: 22px; margin: 0; color: #fff;">${StringKey.TIER_STANDARD.tr()}</h2>
                        <p style="color: #99a0a6; font-size: 14px; margin: 4px 0 0;">${StringKey.TIER_STANDARD_SUBTITLE.tr()}</p>
                    </div>
                    <div style="display: flex; flex-direction: ${if (isMobile) "column" else "row"}; gap: $gap; justify-content: center; align-items: stretch; margin-bottom: 40px;">
                        <!-- Standard Monthly -->
                        <div style="background: #1a1a2e; border: 1px solid #333; border-radius: 16px; padding: 32px 24px; width: $cardWidth; text-align: center;">
                            <h3 style="font-size: 20px; margin: 0 0 8px;">${StringKey.SUBSCRIBE_MONTHLY.tr()}</h3>
                            <div style="font-size: 36px; font-weight: 700; margin: 16px 0;">
                                ${'$'}6.99<span style="font-size: 16px; color: #99a0a6; font-weight: 400;">${StringKey.SUBSCRIBE_PER_MONTH.tr()}</span>
                            </div>
                            <p style="color: #99a0a6; font-size: 14px; margin-bottom: 16px;">${StringKey.SUBSCRIBE_TRIAL_INCLUDED.tr()}</p>
                            <ul style="list-style:none;padding:0;margin:0 0 24px;text-align:left;">
                                <li style="padding:4px 0;color:#ccc;font-size:13px;"><span style="color:#00d9ff;">✓</span> ${StringKey.PRICING_UNLIMITED_ACCOUNTS.tr()}</li>
                                <li style="padding:4px 0;color:#ccc;font-size:13px;"><span style="color:#00d9ff;">✓</span> ${StringKey.PRICING_REALTIME_TRACKING.tr()}</li>
                                <li style="padding:4px 0;color:#ccc;font-size:13px;"><span style="color:#00d9ff;">✓</span> ${StringKey.PRICING_AI_ADVICE_CARDS.tr()}</li>
                                <li style="padding:4px 0;color:#ccc;font-size:13px;"><span style="color:#00d9ff;">✓</span> ${StringKey.PRICING_UNLIMITED_GOALS.tr()}</li>
                                <li style="padding:4px 0;color:#ccc;font-size:13px;"><span style="color:#00d9ff;">✓</span> ${StringKey.PRICING_GOAL_OPTIMIZATION.tr()}</li>
                                <li style="padding:4px 0;color:#ccc;font-size:13px;"><span style="color:#00d9ff;">✓</span> ${StringKey.PRICING_CROSS_PLATFORM.tr()}</li>
                                <li style="padding:4px 0;color:#ccc;font-size:13px;"><span style="color:#00d9ff;">✓</span> ${StringKey.PRICING_CATEGORY_CUSTOMIZATION.tr()}</li>
                            </ul>
                            <button id="plan-monthly" style="background: transparent; color: #fff; border: 2px solid #555; border-radius: 8px; padding: 14px 32px; font-size: 16px; font-weight: 700; cursor: pointer; width: 100%;">$buttonLabel</button>
                        </div>
                        <!-- Standard Yearly -->
                        <div style="background: #1a1a2e; border: 1px solid #333; border-radius: 16px; padding: 32px 24px; width: $cardWidth; text-align: center; position: relative;">
                            <h3 style="font-size: 20px; margin: 0 0 8px;">${StringKey.SUBSCRIBE_YEARLY.tr()}</h3>
                            <div style="font-size: 36px; font-weight: 700; margin: 16px 0;">
                                ${'$'}72<span style="font-size: 16px; color: #99a0a6; font-weight: 400;">${StringKey.SUBSCRIBE_PER_YEAR.tr()}</span>
                            </div>
                            <p style="color: #33cc33; font-size: 14px; margin-bottom: 16px;">${StringKey.SUBSCRIBE_SAVE_PERCENT.tr()}</p>
                            <ul style="list-style:none;padding:0;margin:0 0 24px;text-align:left;">
                                <li style="padding:4px 0;color:#ccc;font-size:13px;"><span style="color:#00d9ff;">✓</span> ${StringKey.PRICING_UNLIMITED_ACCOUNTS.tr()}</li>
                                <li style="padding:4px 0;color:#ccc;font-size:13px;"><span style="color:#00d9ff;">✓</span> ${StringKey.PRICING_REALTIME_TRACKING.tr()}</li>
                                <li style="padding:4px 0;color:#ccc;font-size:13px;"><span style="color:#00d9ff;">✓</span> ${StringKey.PRICING_AI_ADVICE_CARDS.tr()}</li>
                                <li style="padding:4px 0;color:#ccc;font-size:13px;"><span style="color:#00d9ff;">✓</span> ${StringKey.PRICING_UNLIMITED_GOALS.tr()}</li>
                                <li style="padding:4px 0;color:#ccc;font-size:13px;"><span style="color:#00d9ff;">✓</span> ${StringKey.PRICING_GOAL_OPTIMIZATION.tr()}</li>
                                <li style="padding:4px 0;color:#ccc;font-size:13px;"><span style="color:#00d9ff;">✓</span> ${StringKey.PRICING_CROSS_PLATFORM.tr()}</li>
                                <li style="padding:4px 0;color:#ccc;font-size:13px;"><span style="color:#00d9ff;">✓</span> ${StringKey.PRICING_CATEGORY_CUSTOMIZATION.tr()}</li>
                            </ul>
                            <button id="plan-yearly" style="background: transparent; color: #fff; border: 2px solid #555; border-radius: 8px; padding: 14px 32px; font-size: 16px; font-weight: 700; cursor: pointer; width: 100%;">$buttonLabel</button>
                        </div>
                    </div>
        """

        val navHtml = if (!asModal && !isMobile && onNavigate != null) {
            MarketingMenu.hamburgerButtonHtml() + MarketingMenu.overlayHtml(currentPage = "subscribe", isLoggedIn = isLoggedIn)
        } else {
            ""
        }
        // Full-page paywall always needs a way out (a desktop user who navigated
        // to /subscribe used to be stranded — only the hamburger escaped). It sits
        // centered right under the plan cards, above the auto-renew disclosure, so
        // it doesn't crowd the logo. The parent is text-align:center, so the
        // inline-block button centers itself.
        val backBtnHtml = if (!asModal) {
            val label = if (isLoggedIn) StringKey.PAYWALL_MAYBE_LATER.tr() else StringKey.GUIDE_BACK.tr()
            """<button id="subscribe-back" style="background: none; border: 1px solid #333; color: #99a0a6; cursor: pointer; font-size: 15px; padding: 10px 20px; margin-top: 32px; border-radius: 8px; display: inline-block;">$label</button>"""
        } else ""

        return """
            $navHtml
            <div style="max-width: 800px; width: 100%; text-align: center;">
                <a href="/" style="display: inline-block; margin-bottom: 20px;">
                    <img src="wiz3r_logo.png?v=3" alt="wiz≡r" style="height: 56px; cursor: pointer;" />
                </a>

                <h1 style="font-size: ${if (isMobile) "28px" else "36px"}; margin-bottom: 8px;">$title</h1>
                <p style="color: #99a0a6; font-size: 16px; margin-bottom: 40px;">$subtitle</p>

                $standardSection

                <!-- Pro Tier Header -->
                <div style="width: 100%; max-width: 720px; margin: 0 auto 12px; text-align: center;">
                    <h2 style="font-size: 22px; margin: 0; color: #00d9ff;">${StringKey.TIER_PRO.tr()}</h2>
                    <p style="color: #99a0a6; font-size: 14px; margin: 4px 0 0;">${StringKey.TIER_PRO_SUBTITLE.tr()}</p>
                </div>
                <div style="display: flex; flex-direction: ${if (isMobile) "column" else "row"}; gap: $gap; justify-content: center; align-items: stretch;">
                    <!-- Pro Monthly -->
                    <div style="background: #1a1a2e; border: 1px solid #00d9ff; border-radius: 16px; padding: 32px 24px; width: $cardWidth; text-align: center;">
                        <h3 style="font-size: 20px; margin: 0 0 8px;">${StringKey.SUBSCRIBE_MONTHLY.tr()}</h3>
                        <div style="font-size: 36px; font-weight: 700; margin: 16px 0;">
                            ${'$'}12.99<span style="font-size: 16px; color: #99a0a6; font-weight: 400;">${StringKey.SUBSCRIBE_PER_MONTH.tr()}</span>
                        </div>
                        ${if (!proOnly) """<p style="color: #99a0a6; font-size: 14px; margin-bottom: 16px;">${StringKey.SUBSCRIBE_TRIAL_INCLUDED.tr()}</p>""" else ""}
                        <ul style="list-style:none;padding:0;margin:0 0 24px;text-align:left;">
                            <li style="padding:4px 0;color:#ccc;font-size:13px;"><span style="color:#00d9ff;">✓</span> ${StringKey.PRICING_EVERYTHING_IN_STANDARD.tr()}</li>
                            <li style="padding:4px 0;color:#fff;font-size:13px;font-weight:500;"><span style="color:#00d9ff;">✓</span> ${StringKey.PRICING_MULTI_SELECT_AND_BATCH.tr()}</li>
                            <li style="padding:4px 0;color:#fff;font-size:13px;font-weight:500;"><span style="color:#00d9ff;">✓</span> ${StringKey.PRICING_TRANSACTION_NOTIFICATIONS.tr()}</li>
                            <li style="padding:4px 0;color:#fff;font-size:13px;font-weight:500;"><span style="color:#00d9ff;">✓</span> ${StringKey.PRICING_SPLIT_TRANSACTIONS.tr()}</li>
                            <li style="padding:4px 0;color:#fff;font-size:13px;font-weight:500;"><span style="color:#00d9ff;">✓</span> ${StringKey.PRICING_DISTRIBUTE_TRANSACTIONS.tr()}</li>
                            <li style="padding:4px 0;color:#fff;font-size:13px;font-weight:500;"><span style="color:#00d9ff;">✓</span> ${StringKey.PRICING_CUSTOMIZABLE_DASHBOARD.tr()}</li>
                            <li style="padding:4px 0;color:#fff;font-size:13px;font-weight:500;"><span style="color:#00d9ff;">✓</span> ${StringKey.PRICING_CONTEXT_FILTERS.tr()}</li>
                        </ul>
                        <button id="plan-pro-monthly" style="background: #00d9ff; color: #0a0a0a; border: none; border-radius: 8px; padding: 14px 32px; font-size: 16px; font-weight: 700; cursor: pointer; width: 100%;">$buttonLabel</button>
                    </div>
                    <!-- Pro Yearly -->
                    <div style="background: #1a1a2e; border: 2px solid #00d9ff; border-radius: 16px; padding: 32px 24px; width: $cardWidth; text-align: center; position: relative; box-shadow: 0 0 30px rgba(0,217,255,0.15);">
                        <div style="position: absolute; top: -12px; left: 50%; transform: translateX(-50%); background: #00d9ff; color: #0a0a0a; padding: 4px 16px; border-radius: 12px; font-size: 12px; font-weight: 700;">${StringKey.PRICING_BEST_VALUE.tr()}</div>
                        <h3 style="font-size: 20px; margin: 0 0 8px;">${StringKey.SUBSCRIBE_YEARLY.tr()}</h3>
                        <div style="font-size: 36px; font-weight: 700; margin: 16px 0;">
                            ${'$'}132<span style="font-size: 16px; color: #99a0a6; font-weight: 400;">${StringKey.SUBSCRIBE_PER_YEAR.tr()}</span>
                        </div>
                        <p style="color: #00d9ff; font-size: 14px; margin-bottom: 16px;">${StringKey.PRICING_SAVE_15.tr()}</p>
                        <ul style="list-style:none;padding:0;margin:0 0 24px;text-align:left;">
                            <li style="padding:4px 0;color:#ccc;font-size:13px;"><span style="color:#00d9ff;">✓</span> ${StringKey.PRICING_EVERYTHING_IN_STANDARD.tr()}</li>
                            <li style="padding:4px 0;color:#fff;font-size:13px;font-weight:500;"><span style="color:#00d9ff;">✓</span> ${StringKey.PRICING_MULTI_SELECT_AND_BATCH.tr()}</li>
                            <li style="padding:4px 0;color:#fff;font-size:13px;font-weight:500;"><span style="color:#00d9ff;">✓</span> ${StringKey.PRICING_TRANSACTION_NOTIFICATIONS.tr()}</li>
                            <li style="padding:4px 0;color:#fff;font-size:13px;font-weight:500;"><span style="color:#00d9ff;">✓</span> ${StringKey.PRICING_SPLIT_TRANSACTIONS.tr()}</li>
                            <li style="padding:4px 0;color:#fff;font-size:13px;font-weight:500;"><span style="color:#00d9ff;">✓</span> ${StringKey.PRICING_DISTRIBUTE_TRANSACTIONS.tr()}</li>
                            <li style="padding:4px 0;color:#fff;font-size:13px;font-weight:500;"><span style="color:#00d9ff;">✓</span> ${StringKey.PRICING_CUSTOMIZABLE_DASHBOARD.tr()}</li>
                            <li style="padding:4px 0;color:#fff;font-size:13px;font-weight:500;"><span style="color:#00d9ff;">✓</span> ${StringKey.PRICING_CONTEXT_FILTERS.tr()}</li>
                        </ul>
                        <button id="plan-pro-yearly" style="background: #00d9ff; color: #0a0a0a; border: none; border-radius: 8px; padding: 14px 32px; font-size: 16px; font-weight: 700; cursor: pointer; width: 100%;">$buttonLabel</button>
                        <p style="color: #666; font-size: 11px; margin-top: 8px;">${StringKey.PRICING_PRO_YEARLY_QUARTERLY_NOTE.tr()}</p>
                    </div>
                </div>

                $backBtnHtml

                ${if (!proOnly) """<p style="color: #666; font-size: 12px; margin-top: 32px;">${StringKey.SUBSCRIBE_TRIAL_DISCLAIMER.tr()}</p>""" else ""}

                <div style="margin-top: 24px; text-align: center;">
                    <p style="color: #666; font-size: 11px; margin: 0 auto 8px; max-width: 560px; line-height: 1.5;">
                        ${StringKey.PAYWALL_AUTO_RENEW_DISCLOSURE.tr()}
                    </p>
                    <p style="font-size: 12px; margin: 0;">
                        <a href="/terms" style="color: #00d9ff; text-decoration: none; margin: 0 12px;">${StringKey.PAYWALL_TERMS_OF_USE.tr()}</a>
                        <a href="/privacy" style="color: #00d9ff; text-decoration: none; margin: 0 12px;">${StringKey.PAYWALL_PRIVACY_POLICY.tr()}</a>
                    </p>
                </div>
            </div>
        """
    }

    fun attachEventListeners(element: HTMLElement) {
        attachButtonListeners(element)
        if (!asModal && !ResponsiveUtils.isMobile() && onNavigate != null) {
            window.setTimeout({
                MarketingMenu.attachEventListeners(
                    onNavigate = onNavigate,
                    isLoggedIn = isLoggedIn,
                    onAppNavigate = onAppNavigate,
                )
            }, 100)
        }
    }

    private fun attachButtonListeners(element: HTMLElement) {
        element.querySelector("#subscribe-back")?.addEventListener("click", { onBackClick() })
        // Standard plan buttons
        element.querySelector("#plan-monthly")?.addEventListener("click", { startCheckout("monthly", "standard") })
        element.querySelector("#plan-yearly")?.addEventListener("click", { startCheckout("yearly", "standard") })
        // Pro plan buttons
        element.querySelector("#plan-pro-monthly")?.addEventListener("click", { startCheckout("monthly", "pro") })
        element.querySelector("#plan-pro-yearly")?.addEventListener("click", { startCheckout("yearly", "pro") })
    }

    private fun startCheckout(plan: String, tier: String = "standard") {
        val baseUrl = window.location.origin
        val token = ApiService.getSharedAuthToken()
        if (token == null) {
            console.error("No auth token available for checkout")
            return
        }

        GlobalScope.launch {
            try {
                val body = json.encodeToString(
                    CheckoutRequest(
                        plan = plan,
                        tier = tier,
                        successUrl = "$baseUrl/#subscribe-success",
                        cancelUrl = "$baseUrl/#subscribe-cancel",
                    ),
                )
                val response = window.fetch(
                    "$baseUrl/api/subscribe/checkout",
                    RequestInit(
                        method = "POST",
                        headers = js("({'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token})"),
                        body = body,
                    ),
                ).await()

                if (response.ok) {
                    val text = response.text().await()
                    val checkout = json.decodeFromString<CheckoutResponse>(text)
                    window.location.href = checkout.url
                } else {
                    console.error("Checkout failed: ${response.status}")
                }
            } catch (e: Exception) {
                console.error("Checkout error: ${e.message}")
            }
        }
    }
}

class SubscribeSuccessPage(private val onContinueClick: () -> Unit) {
    fun render(): HTMLElement {
        val container = document.createElement("div") as HTMLElement
        container.style.cssText = """
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
            background: transparent; color: #ffffff; min-height: 100vh;
            display: flex; flex-direction: column; align-items: center; justify-content: center;
            padding: 24px; text-align: center;
        """
        container.innerHTML = """
            <div style="font-size: 64px; margin-bottom: 24px;">🎉</div>
            <h1 style="font-size: 32px; margin-bottom: 8px;">${StringKey.SUBSCRIBE_SUCCESS_TITLE.tr()}</h1>
            <p style="color: #99a0a6; font-size: 16px; margin-bottom: 32px;">
                ${StringKey.SUBSCRIBE_SUCCESS_DESC.tr()}
            </p>
            <button id="success-continue" style="
                background: #00d9ff; color: #0a0a0a; border: none; border-radius: 8px;
                padding: 14px 32px; font-size: 16px; font-weight: 700; cursor: pointer;
            ">${StringKey.SUBSCRIBE_GO_DASHBOARD.tr()}</button>
        """
        return container
    }

    fun attachEventListeners(element: HTMLElement) {
        element.querySelector("#success-continue")?.addEventListener("click", { onContinueClick() })
    }
}

class SubscribeCancelPage(private val onRetryClick: () -> Unit, private val onBackClick: () -> Unit) {
    fun render(): HTMLElement {
        val container = document.createElement("div") as HTMLElement
        container.style.cssText = """
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
            background: transparent; color: #ffffff; min-height: 100vh;
            display: flex; flex-direction: column; align-items: center; justify-content: center;
            padding: 24px; text-align: center;
        """
        container.innerHTML = """
            <h1 style="font-size: 32px; margin-bottom: 8px;">${StringKey.SUBSCRIBE_CANCEL_TITLE.tr()}</h1>
            <p style="color: #99a0a6; font-size: 16px; margin-bottom: 32px;">
                ${StringKey.SUBSCRIBE_CANCEL_DESC.tr()}
            </p>
            <div style="display: flex; gap: 16px;">
                <button id="cancel-retry" style="
                    background: #00d9ff; color: #0a0a0a; border: none; border-radius: 8px;
                    padding: 14px 24px; font-size: 16px; font-weight: 700; cursor: pointer;
                ">${StringKey.SUBSCRIBE_TRY_AGAIN.tr()}</button>
                <button id="cancel-back" style="
                    background: transparent; color: #00d9ff; border: 1px solid #333; border-radius: 8px;
                    padding: 14px 24px; font-size: 16px; cursor: pointer;
                ">${StringKey.SUBSCRIBE_GO_BACK.tr()}</button>
            </div>
        """
        return container
    }

    fun attachEventListeners(element: HTMLElement) {
        element.querySelector("#cancel-retry")?.addEventListener("click", { onRetryClick() })
        element.querySelector("#cancel-back")?.addEventListener("click", { onBackClick() })
    }
}
